// apis
import { groupCartLockApi } from "@cart-client/apis/GroupCartOrder";

// interfaces
import { ApiResponse } from "@cart-client/interfaces/Api";

// internal-deps
import { Err, Ok, Result } from "@swiggy-private/js-fp";
import { LockGroupCartOrderResponse } from "@swiggy-private/cart-types";
import {
    CheckoutActionTypes,
    CheckoutContext,
    NEWRELIC_TABLES,
    recordNREvent,
    GroupCartActionTypes,
    GroupOrderCartContext,
} from "@swiggy-private/checkout-core";

// deps
import { useCallback, useContext, useState } from "react";

// reducer
import { CartActionTypes } from "@cart-client/reducers/CartReducer";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// constants
import { OOPS_SOMETHING_WENT_WRONG } from "@cart-client/constants/TextDict";

interface useLockGroupCartReturn {
    transitionToCart: (userId: string) => void;
    checkoutInProgress: boolean;
}

export function transitionUserToCartApi(
    groupCartId: string,
    hostUserId: string,
): Promise<Result<LockGroupCartOrderResponse["data"], Error | ApiResponse>> {
    return groupCartLockApi(groupCartId, hostUserId)
        .then(response => {
            return Ok(response);
        })
        .catch(err => {
            return Err(err);
        });
}

const useLockGroupCart = (
    groupCartId: string,
    enablePolling: VoidFunction,
    disablePolling: VoidFunction,
): useLockGroupCartReturn => {
    const { setCheckoutState } = useContext(CheckoutContext);
    const { setCartState } = useContext(CartContext);
    const { setState } = useContext(GroupOrderCartContext);
    const [checkoutInProgress, setCheckoutInProgress] = useState(false);

    const transitionToCart = useCallback(
        async (hostUserId: string) => {
            try {
                setCheckoutInProgress(true);
                disablePolling();

                const data = await transitionUserToCartApi(groupCartId, hostUserId);

                if (data.isErr()) {
                    throw data;
                }

                const transitionCartResponse = data.unwrap();
                const mergedCartPayload = transitionCartResponse.merged_cart_payload;
                setState({
                    type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                    payload: {
                        isCartLocked: true,
                    },
                });
                setCartState({
                    type: CartActionTypes.UPDATE_LOCAL_CART,
                    payload: mergedCartPayload,
                });
                setCheckoutState({
                    type: CheckoutActionTypes.SHOW_CART,
                    payload: null,
                });
                setCheckoutInProgress(false);
                recordNREvent({
                    eventType: NEWRELIC_TABLES.GROUP_CART_TO_CHECKOUT_TRANSITION,
                });
            } catch (err) {
                setState({
                    type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                    payload: {
                        softError: OOPS_SOMETHING_WENT_WRONG,
                    },
                });
                setCheckoutInProgress(false);

                recordNREvent({
                    eventType: NEWRELIC_TABLES.GROUP_CART_LOCK_API_FAILURE,
                    error: JSON.stringify(err),
                });

                // re-enable back polling if discontinued
                enablePolling();
            }
        },
        [disablePolling, enablePolling, groupCartId, setCartState, setCheckoutState, setState],
    );

    return {
        transitionToCart,
        checkoutInProgress,
    };
};

export default useLockGroupCart;
